import { useMemo, useState } from "react"
import { api, type Comment, type CommentAttachment } from "../lib/api"
import { resolveAttachmentHref } from "../lib/attachment-refs"
import { CommentEditor } from "./CommentEditor"
import { Markdown } from "./markdown"

type CommentDisplayProps = {
  comment: Comment
  onUpdate: () => void
  onDelete: () => void
  canEdit: boolean
  canDelete: boolean
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function CommentDisplay({
  comment,
  onUpdate,
  onDelete,
  canEdit,
  canDelete,
}: CommentDisplayProps) {
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const attachments = comment.attachments ?? []

  // An image uploaded from inside the editor is stored as `attachment:<id>`,
  // because the signed URL that would otherwise be in the body expires in an
  // hour. This is what turns it back into a URL, freshly, on every render.
  const resolveHref = useMemo(() => resolveAttachmentHref(attachments), [attachments])

  // A file the body already shows inline does not also need a card under it.
  const cards = useMemo(
    () => attachments.filter((a) => !comment.body.includes(`attachment:${a.id}`)),
    [attachments, comment.body],
  )

  async function handleUpdate(
    body: string,
    removeAttachmentIds: string[],
    attachmentIds: string[]
  ) {
    const formData = new FormData()
    formData.append("body", body)
    if (removeAttachmentIds.length > 0) {
      formData.append("removeAttachmentIds", JSON.stringify(removeAttachmentIds))
    }
    if (attachmentIds.length > 0) {
      formData.append("attachmentIds", JSON.stringify(attachmentIds))
    }

    await api.patchMultipart(`/projects/comments/${comment.id}`, formData)
    setEditing(false)
    onUpdate()
  }

  async function handleDelete() {
    if (!confirm("Delete this comment?")) return
    setDeleting(true)
    try {
      await api.delete(`/projects/comments/${comment.id}`)
      onDelete()
    } finally {
      setDeleting(false)
    }
  }

  async function handleDeleteAttachment(attachmentId: string) {
    if (!confirm("Delete this attachment?")) return
    await api.delete(`/projects/attachments/${attachmentId}`)
    onUpdate()
  }

  if (editing) {
    return (
      <CommentEditor
        initialBody={comment.body}
        initialAttachments={comment.attachments || []}
        onSubmit={handleUpdate}
        onCancel={() => setEditing(false)}
        placeholder="Edit comment..."
        compact
      />
    )
  }

  return (
    <div className="group relative flex gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{comment.authorName}</span>
          <span className="text-xs text-muted-foreground">
            {new Date(comment.createdAt).toLocaleString()}
          </span>
          {comment.source === "agent" && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              Agent
            </span>
          )}
        </div>

        <div className="mt-1">
          <Markdown resolveHref={resolveHref}>{comment.body}</Markdown>
        </div>

        {/* Attachments */}
        {cards.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {cards.map((att) => (
              <AttachmentCard
                key={att.id}
                attachment={att}
                canDelete={canDelete}
                onDelete={() => handleDeleteAttachment(att.id)}
              />
            ))}
          </div>
        )}

        {/* Actions */}
        {(canEdit || canDelete) && (
          <div className="mt-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="text-xs text-destructive hover:text-destructive/80 disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function AttachmentCard({
  attachment,
  canDelete,
  onDelete,
}: {
  attachment: CommentAttachment
  canDelete: boolean
  onDelete: () => void
}) {
  // Null when the server has no storage configured. The card still renders —
  // the row exists and saying so is more useful than a broken image — but
  // there is nothing to link to.
  const downloadUrl = attachment.url
  // Only a safe preview is painted as a thumbnail. An SVG is an image too, but
  // it is served as a download, so it gets the file icon like everything else.
  const isImage = attachment.inline && attachment.mimeType.startsWith("image/")

  return (
    <div className="group/att relative flex items-center gap-2 rounded-md border border-border bg-muted p-2">
      {isImage && downloadUrl ? (
        <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={downloadUrl}
            alt={attachment.filename}
            className="h-12 w-12 rounded object-cover hover:opacity-80"
          />
        </a>
      ) : (
        <div className="flex h-12 w-12 items-center justify-center rounded bg-background">
          <svg
            className="h-6 w-6 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
      )}

      <div className="flex-1 min-w-0">
        {downloadUrl ? (
          <a
            href={downloadUrl}
            download={attachment.filename}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate text-sm font-medium hover:underline"
          >
            {attachment.filename}
          </a>
        ) : (
          <span
            className="block truncate text-sm font-medium text-muted-foreground"
            title="File storage is not configured on this server"
          >
            {attachment.filename}
          </span>
        )}
        <p className="text-xs text-muted-foreground">{formatFileSize(attachment.sizeBytes)}</p>
      </div>

      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute right-1 top-1 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover/att:opacity-100"
          title="Delete attachment"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  )
}
